import {useCallback, useEffect, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';
import {PATHS} from 'routes/constants';
import {usePath} from './common';

type ReturnTarget = {path: string; state?: unknown};

type RegistrationReturnStore = {
  target: ReturnTarget | null;
  setTarget: (target: ReturnTarget) => void;
  clear: () => void;
};

type ReturnLocationState = {callbackUrl?: string; callbackState?: unknown} | null;

const REGISTRATION_FLOW_PATHS = [
  PATHS.guestRegistration,
  PATHS.addPersonalDataForm,
  PATHS.verification.main,
  PATHS.auth.root,
];

const useRegistrationReturnStore = create<RegistrationReturnStore>()(
  persist(
    set => ({
      target: null,
      setTarget: target => set({target}),
      clear: () => set({target: null}),
    }),
    {
      name: 'chekin-registration-return',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

function useClearRegistrationReturnOutsideFlow() {
  const {pathname} = useLocation();
  const clear = useRegistrationReturnStore(state => state.clear);

  useEffect(() => {
    const isInFlow = REGISTRATION_FLOW_PATHS.some(path => pathname.includes(`/${path}`));
    if (!isInFlow) clear();
  }, [pathname, clear]);
}

function useRegistrationReturn() {
  const {state} = useLocation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const storedTarget = useRegistrationReturnStore(store => store.target);
  const setTarget = useRegistrationReturnStore(store => store.setTarget);

  const {callbackUrl, callbackState} = (state as ReturnLocationState) ?? {};
  useEffect(() => {
    if (callbackUrl) setTarget({path: callbackUrl, state: callbackState});
  }, [callbackUrl, callbackState, setTarget]);

  const target = useMemo(
    () => (callbackUrl ? {path: callbackUrl, state: callbackState} : storedTarget),
    [callbackUrl, callbackState, storedTarget],
  );
  const isFromTaxes = Boolean(target?.path.startsWith(getFullPath(PATHS.taxes.main)));

  const goBackToTaxes = useCallback(() => {
    if (target) navigate(target.path, {state: target.state});
  }, [navigate, target]);

  return {isFromTaxes, goBackToTaxes};
}

export {useClearRegistrationReturnOutsideFlow, useRegistrationReturn};
